import {
  CancelRounded,
  CheckCircleOutlined,
  CloseSharp,
  DeleteSharp,
  EditSharp,
  MoreVertOutlined,
  SearchSharp,
  VisibilitySharp,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useMemo, useReducer } from "react";

import AddressForm from "../../components/crm/AddressForm";
import EmptyState from "../../components/crm/EmptyState";
import TableLoadingSkeleton, {
  type TableSkeletonColumn,
} from "../../components/crm/TableLoadingSkeleton";
import CustomConfirmDialog from "../../components/CustomConfirmDialog";
import CustomDialog from "../../components/CustomDialog";
import { useSnackbar } from "../../contexts/SnackbarContext";
import useCrmAddresses from "../../hooks/useCrmAddresses";
import {
  bulkDeleteAddresses,
  bulkUpdateAddressStatus,
  createAddress,
  deleteAddress,
  updateAddress,
} from "../../services/crm.service";
import { formatThaiPhone } from "../../utils/phone";
import {
  AddressFilters,
  AddressStatus,
  CustomerAddress,
  CustomerAddressCreateRequest,
  UpdateCustomerAddressRequest,
} from "../../types/crm";

type LocationListFilters = Omit<AddressFilters, "status"> & {
  status: AddressStatus | "all";
};

const LOCATION_STATUS_CHIP_CONFIG: Record<
  AddressStatus,
  { label: string; bg: string; color: string }
> = {
  active: { label: "Active", bg: "#E3F5E3", color: "#2D6830" },
  pending: { label: "Pending Approval", bg: "#FFFBC5", color: "#BB6902" },
  rejected: { label: "Reject", bg: "#FFE3E1", color: "#BE2217" },
};

const LOCATIONS_SKELETON_COLUMNS: TableSkeletonColumn[] = [
  { key: "checkbox", isCheckbox: true },
  { key: "actions", width: 80, align: "center" },
  { key: "no", width: 96, align: "center" },
  { key: "ecp-code", width: 140 },
  { key: "name", width: 180 },
  { key: "phone", width: 140, align: "center" },
  { key: "shop-name", width: 160 },
  { key: "address", width: 200 },
  { key: "province", width: 140 },
  { key: "zip-code", width: 100, align: "center" },
  { key: "default", width: 100, align: "center" },
  { key: "status", width: 120, align: "center" },
  { key: "created-date", width: 153, align: "center" },
];

export default function CrmLocations() {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const getFullAddressText = (address: CustomerAddress) =>
    [
      address.address,
      address.sub_district,
      address.district,
      address.province,
      address.zip_code,
    ]
      .filter((value) => !!value)
      .join(", ");

  type State = {
    filters: LocationListFilters;
    dialogOpen: boolean;
    viewDialogOpen: boolean;
    deleteDialogOpen: boolean;
    approveDialogOpen: boolean;
    bulkDeleteDialogOpen: boolean;
    rejectDialogOpen: boolean;
    rejectRemark: string;
    selectedAddress: CustomerAddress | null;
    isSubmitting: boolean;
    selectedIds: string[];
    actionAnchorEl: HTMLElement | null;
    actionRow: CustomerAddress | null;
  };

  type Action =
    | { type: "SET_FILTERS"; payload: Partial<LocationListFilters> }
    | { type: "SET_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_VIEW_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_DELETE_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_APPROVE_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_BULK_DELETE_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_REJECT_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_REJECT_REMARK"; payload: string }
    | { type: "SET_SELECTED_ADDRESS"; payload: CustomerAddress | null }
    | { type: "SET_IS_SUBMITTING"; payload: boolean }
    | { type: "SET_SELECTED_IDS"; payload: string[] }
    | { type: "SET_ACTION_ANCHOR_EL"; payload: HTMLElement | null }
    | { type: "SET_ACTION_ROW"; payload: CustomerAddress | null }
    | {
        type: "OPEN_ACTION_MENU";
        payload: { anchorEl: HTMLElement; row: CustomerAddress };
      }
    | { type: "CLOSE_DIALOGS" };

  const initialState: State = {
    filters: {
      search: "",
      status: "all",
      page: 0,
      page_size: 10,
    },
    dialogOpen: false,
    viewDialogOpen: false,
    deleteDialogOpen: false,
    approveDialogOpen: false,
    bulkDeleteDialogOpen: false,
    rejectDialogOpen: false,
    rejectRemark: "",
    selectedAddress: null,
    isSubmitting: false,
    selectedIds: [],
    actionAnchorEl: null,
    actionRow: null,
  };

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case "SET_FILTERS":
        return {
          ...state,
          filters: { ...state.filters, ...action.payload },
          selectedIds: [],
        };
      case "SET_DIALOG_OPEN":
        return { ...state, dialogOpen: action.payload };
      case "SET_VIEW_DIALOG_OPEN":
        return { ...state, viewDialogOpen: action.payload };
      case "SET_DELETE_DIALOG_OPEN":
        return { ...state, deleteDialogOpen: action.payload };
      case "SET_APPROVE_DIALOG_OPEN":
        return { ...state, approveDialogOpen: action.payload };
      case "SET_BULK_DELETE_DIALOG_OPEN":
        return { ...state, bulkDeleteDialogOpen: action.payload };
      case "SET_REJECT_DIALOG_OPEN":
        return { ...state, rejectDialogOpen: action.payload };
      case "SET_REJECT_REMARK":
        return { ...state, rejectRemark: action.payload };
      case "SET_SELECTED_ADDRESS":
        return { ...state, selectedAddress: action.payload };
      case "SET_IS_SUBMITTING":
        return { ...state, isSubmitting: action.payload };
      case "SET_SELECTED_IDS":
        return { ...state, selectedIds: action.payload };
      case "SET_ACTION_ANCHOR_EL":
        return { ...state, actionAnchorEl: action.payload };
      case "SET_ACTION_ROW":
        return { ...state, actionRow: action.payload };
      case "OPEN_ACTION_MENU":
        return {
          ...state,
          actionAnchorEl: action.payload.anchorEl,
          actionRow: action.payload.row,
        };
      case "CLOSE_DIALOGS":
        return {
          ...state,
          dialogOpen: false,
          viewDialogOpen: false,
          deleteDialogOpen: false,
          approveDialogOpen: false,
          bulkDeleteDialogOpen: false,
          rejectDialogOpen: false,
        };
      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    filters,
    dialogOpen,
    viewDialogOpen,
    deleteDialogOpen,
    approveDialogOpen,
    bulkDeleteDialogOpen,
    rejectDialogOpen,
    rejectRemark,
    selectedAddress,
    isSubmitting,
    selectedIds,
    actionAnchorEl,
    actionRow,
  } = state;

  const apiFilters = useMemo(
    () => ({
      ...filters,
      status: filters.status === "all" ? undefined : filters.status,
    }),
    [filters],
  );

  const { data, dataLoading, mutate } = useCrmAddresses(true, null, apiFilters);

  const addresses: CustomerAddress[] = data?.data || [];
  const totalRecords = data?.total_records || 0;
  const isLoading = dataLoading;

  const handleSearchChange = useCallback((value: string) => {
    dispatch({ type: "SET_FILTERS", payload: { search: value, page: 0 } });
  }, []);

  const handleStatusChange = useCallback((value: string) => {
    dispatch({
      type: "SET_FILTERS",
      payload: {
        status: value as AddressStatus | "all",
        page: 0,
      },
    });
  }, []);

  const handlePageChange = useCallback((_: unknown, page: number) => {
    dispatch({ type: "SET_FILTERS", payload: { page } });
  }, []);

  const handlePageSizeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: "SET_FILTERS",
        payload: {
          page_size: parseInt(event.target.value, 10),
          page: 0,
        },
      });
    },
    [],
  );

  const handleAddNew = () => {
    dispatch({ type: "SET_SELECTED_ADDRESS", payload: null });
    dispatch({ type: "SET_DIALOG_OPEN", payload: true });
  };

  const handleEdit = (address: CustomerAddress) => {
    dispatch({ type: "SET_SELECTED_ADDRESS", payload: address });
    dispatch({ type: "SET_DIALOG_OPEN", payload: true });
  };

  const handleView = (address: CustomerAddress) => {
    dispatch({ type: "SET_SELECTED_ADDRESS", payload: address });
    dispatch({ type: "SET_VIEW_DIALOG_OPEN", payload: true });
  };

  const handleDeleteClick = (address: CustomerAddress) => {
    dispatch({ type: "SET_SELECTED_ADDRESS", payload: address });
    dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: true });
  };

  const handleFormSubmit = async (
    formData: CustomerAddressCreateRequest | UpdateCustomerAddressRequest,
    ecpCode?: string,
  ) => {
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      if (selectedAddress) {
        await updateAddress(
          selectedAddress.id,
          formData as UpdateCustomerAddressRequest,
        );
      } else {
        const resolvedEcpCode = ecpCode ?? (formData as any).ecp_code;
        if (!resolvedEcpCode) return;
        await createAddress(
          formData as CustomerAddressCreateRequest,
          resolvedEcpCode,
        );
      }
      dispatch({ type: "SET_DIALOG_OPEN", payload: false });
      mutate();
      showSnackbar(
        selectedAddress ? "Address updated successfully" : "Address created successfully",
        "success",
      );
    } catch (error) {
      console.error("Error saving address:", error);
      showSnackbar("Failed to save address", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const handleDelete = async () => {
    if (!selectedAddress) return;
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      await deleteAddress(selectedAddress.id);
      dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_SELECTED_ADDRESS", payload: null });
      mutate();
      showSnackbar("Address deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting address:", error);
      showSnackbar("Failed to delete address", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const handleBulkApprove = () => {
    if (selectedIds.length === 0) return;
    dispatch({ type: "SET_APPROVE_DIALOG_OPEN", payload: true });
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    dispatch({ type: "SET_BULK_DELETE_DIALOG_OPEN", payload: true });
  };

  const handleBulkReject = () => {
    if (selectedIds.length === 0) return;
    dispatch({ type: "SET_REJECT_REMARK", payload: "" });
    dispatch({ type: "SET_REJECT_DIALOG_OPEN", payload: true });
  };

  const handleConfirmApprove = async () => {
    if (selectedIds.length === 0) return;
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      await bulkUpdateAddressStatus({
        address_ids: selectedIds,
        status: "active",
      });
      dispatch({ type: "SET_APPROVE_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
      mutate();
      showSnackbar("Address(es) approved successfully", "success");
    } catch (error) {
      console.error("Error approving addresses:", error);
      showSnackbar("Failed to approve address(es)", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      await bulkDeleteAddresses({ address_ids: selectedIds });
      dispatch({ type: "SET_BULK_DELETE_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
      mutate();
      showSnackbar(`Successfully deleted ${selectedIds.length} address(es)`, "success");
    } catch (error) {
      console.error("Error bulk deleting addresses:", error);
      showSnackbar("Failed to delete address(es)", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const handleConfirmReject = async () => {
    if (selectedIds.length === 0 || !rejectRemark.trim()) return;
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      await bulkUpdateAddressStatus({
        address_ids: selectedIds,
        status: "rejected",
        remark: rejectRemark.trim(),
      });
      dispatch({ type: "SET_REJECT_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_REJECT_REMARK", payload: "" });
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
      mutate();
      showSnackbar("Address(es) rejected successfully", "success");
    } catch (error) {
      console.error("Error rejecting addresses:", error);
      showSnackbar("Failed to reject address(es)", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const filteredAddresses = useMemo(() => {
    return addresses.filter((address) => {
      const searchLower = (filters.search || "").toLowerCase();
      const matchesSearch =
        !searchLower ||
        address.name.toLowerCase().includes(searchLower) ||
        address.surname.toLowerCase().includes(searchLower) ||
        address.ecp_code.toLowerCase().includes(searchLower) ||
        address.shop_name.toLowerCase().includes(searchLower) ||
        address.phone.toLowerCase().includes(searchLower) ||
        address.province.toLowerCase().includes(searchLower);

      const matchesStatus =
        filters.status === "all" || address.status === filters.status;

      return matchesSearch && matchesStatus;
    });
  }, [filters, addresses]);

  const pagedAddresses = filteredAddresses;

  const allSelected =
    pagedAddresses.length > 0 &&
    pagedAddresses.every((row) => selectedIds.includes(row.id));
  const isIndeterminate =
    selectedIds.length > 0 && !allSelected && pagedAddresses.length > 0;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      dispatch({
        type: "SET_SELECTED_IDS",
        payload: pagedAddresses.map((row) => row.id),
      });
    } else {
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
    }
  };

  const handleSelectRow = (id: string) => {
    dispatch({
      type: "SET_SELECTED_IDS",
      payload: selectedIds.includes(id)
        ? selectedIds.filter((rowId: string) => rowId !== id)
        : [...selectedIds, id],
    });
  };

  const handleOpenActionMenu = (
    address: CustomerAddress,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    dispatch({
      type: "OPEN_ACTION_MENU",
      payload: { anchorEl: event.currentTarget, row: address },
    });
  };

  const handleCloseActionMenu = () => {
    dispatch({ type: "SET_ACTION_ANCHOR_EL", payload: null });
    dispatch({ type: "SET_ACTION_ROW", payload: null });
  };

  const handleActionView = () => {
    if (actionRow) handleView(actionRow);
    handleCloseActionMenu();
  };

  const handleActionEdit = () => {
    if (actionRow) handleEdit(actionRow);
    handleCloseActionMenu();
  };

  const handleActionDelete = () => {
    if (actionRow) handleDeleteClick(actionRow);
    handleCloseActionMenu();
  };

  const handleDetailDelete = () => {
    if (!selectedAddress) return;
    dispatch({ type: "SET_VIEW_DIALOG_OPEN", payload: false });
    dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: true });
  };

  const handleDetailApprove = () => {
    if (!selectedAddress) return;
    dispatch({ type: "SET_SELECTED_IDS", payload: [selectedAddress.id] });
    dispatch({ type: "SET_VIEW_DIALOG_OPEN", payload: false });
    dispatch({ type: "SET_APPROVE_DIALOG_OPEN", payload: true });
  };

  const handleDetailReject = () => {
    if (!selectedAddress) return;
    dispatch({ type: "SET_SELECTED_IDS", payload: [selectedAddress.id] });
    dispatch({ type: "SET_REJECT_REMARK", payload: "" });
    dispatch({ type: "SET_VIEW_DIALOG_OPEN", payload: false });
    dispatch({ type: "SET_REJECT_DIALOG_OPEN", payload: true });
  };

  const statusOptions: Array<{ label: string; value: AddressStatus | "all" }> =
    [
      { label: "All Status", value: "all" },
      { label: "Active", value: "active" },
      { label: "Pending Approval", value: "pending" },
      { label: "Reject", value: "rejected" },
    ];

  const renderTable = () => (
    <>
      <TableContainer
        sx={{ borderTop: `1px solid ${theme.vars.palette.divider}` }}
      >
        <Table sx={{ minWidth: 1500, "& td, & th": { whiteSpace: "nowrap" } }}>
          <TableHead>
            <TableRow
              sx={{
                "& .MuiTableCell-root": {
                  height: 52,
                  borderBottom: `1px solid ${theme.vars.palette.divider}`,
                  color: "#25272C",
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  lineHeight: 1.4,
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={isIndeterminate}
                  onChange={(event) => handleSelectAll(event.target.checked)}
                  sx={{
                    "&.Mui-checked": { color: "#1976D2" },
                    "&.MuiCheckbox-indeterminate": { color: "#1976D2" },
                  }}
                />
              </TableCell>
              <TableCell align="center" sx={{ width: 80 }}>
                Actions
              </TableCell>
              <TableCell align="center" sx={{ width: 96 }}>
                No.
              </TableCell>
              <TableCell sx={{ width: 140 }}>ECP Code</TableCell>
              <TableCell sx={{ width: 180 }}>Name</TableCell>
              <TableCell align="center" sx={{ width: 140 }}>
                Phone
              </TableCell>
              <TableCell sx={{ width: 160 }}>Shop Name</TableCell>
              <TableCell sx={{ width: 200 }}>Address</TableCell>
              <TableCell sx={{ width: 140 }}>Province</TableCell>
              <TableCell align="center" sx={{ width: 100 }}>
                Zip Code
              </TableCell>
              <TableCell align="center" sx={{ width: 100 }}>
                Default
              </TableCell>
              <TableCell align="center" sx={{ width: 120 }}>
                Status
              </TableCell>
              <TableCell align="center" sx={{ width: 153 }}>
                Created Date
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedAddresses.map((address, index) => (
              <TableRow
                key={address.id}
                selected={selectedIds.includes(address.id)}
                sx={{
                  "&.Mui-selected, &.Mui-selected:hover": {
                    backgroundColor: "#F9FAFB",
                  },
                  "& .MuiTableCell-root": {
                    height: 79,
                    borderBottom: `1px solid ${theme.vars.palette.divider}`,
                    fontSize: 12,
                    color: "#25272C",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.includes(address.id)}
                    onChange={() => handleSelectRow(address.id)}
                    sx={{ "&.Mui-checked": { color: "#1976D2" } }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ width: 80 }}>
                  <IconButton
                    size="small"
                    onClick={(event) => handleOpenActionMenu(address, event)}
                  >
                    <MoreVertOutlined fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell align="center" sx={{ width: 96 }}>
                  {filters.page && filters.page_size
                    ? index + 1 + filters.page * filters.page_size
                    : index + 1}
                </TableCell>
                <TableCell sx={{ width: 140 }}>{address.ecp_code}</TableCell>
                <TableCell sx={{ width: 180 }}>
                  {address.name} {address.surname}
                </TableCell>
                <TableCell align="center" sx={{ width: 140 }}>
                  {formatThaiPhone(address.phone, "-")}
                </TableCell>
                <TableCell sx={{ width: 160 }}>{address.shop_name}</TableCell>
                <TableCell
                  sx={{
                    width: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {address.address}
                </TableCell>
                <TableCell sx={{ width: 140 }}>{address.province}</TableCell>
                <TableCell align="center" sx={{ width: 100 }}>
                  {address.zip_code}
                </TableCell>
                <TableCell align="center" sx={{ width: 100 }}>
                  {address.default ? (
                    <Chip label="Yes" color="primary" size="small" />
                  ) : (
                    <Chip label="No" size="small" />
                  )}
                </TableCell>
                <TableCell align="center" sx={{ width: 120 }}>
                  <Chip
                    label={LOCATION_STATUS_CHIP_CONFIG[address.status].label}
                    size="small"
                    sx={{
                      height: 24,
                      borderRadius: 1,
                      backgroundColor:
                        LOCATION_STATUS_CHIP_CONFIG[address.status].bg,
                      color: LOCATION_STATUS_CHIP_CONFIG[address.status].color,
                      "& .MuiChip-label": {
                        px: 1,
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: 1.4,
                      },
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ width: 153 }}>
                  {dayjs(address.created_at).format("DD/MM/YYYY HH:mm")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {filteredAddresses.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={filters.page_size || 10}
          page={filters.page || 0}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      )}
    </>
  );

  return (
    <Stack spacing={3}>
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              placeholder="Search"
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ width: 180 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchSharp sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              select
              size="small"
              value={filters.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              SelectProps={{
                renderValue: (value) => {
                  const selectedValue = value as AddressStatus | "all";
                  if (selectedValue === "all") return "All Status";
                  const config = LOCATION_STATUS_CHIP_CONFIG[selectedValue];
                  return (
                    <Chip
                      label={config.label}
                      size="small"
                      sx={{
                        height: 24,
                        borderRadius: 1,
                        backgroundColor: config.bg,
                        color: config.color,
                        "& .MuiChip-label": {
                          px: 1,
                          fontSize: 12,
                          fontWeight: 400,
                          lineHeight: 1.4,
                        },
                      }}
                    />
                  );
                },
              }}
              sx={{
                minWidth: 120,
                maxWidth: 196,
                "& .MuiSelect-select": {
                  color:
                    filters.status === "all"
                      ? "text.secondary"
                      : "text.primary",
                },
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ py: 1.5 }}
                >
                  {option.value === "all" ? (
                    option.label
                  ) : (
                    <Chip
                      label={option.label}
                      size="small"
                      sx={{
                        height: 24,
                        borderRadius: 1,
                        backgroundColor:
                          LOCATION_STATUS_CHIP_CONFIG[option.value].bg,
                        color: LOCATION_STATUS_CHIP_CONFIG[option.value].color,
                        "& .MuiChip-label": {
                          px: 1,
                          fontSize: 12,
                          fontWeight: 400,
                          lineHeight: 1.4,
                        },
                      }}
                    />
                  )}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {selectedIds.length > 0 && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<DeleteSharp />}
                disabled={isSubmitting}
                onClick={handleBulkDelete}
                sx={{
                  height: 40,
                  borderRadius: "96px",
                  borderColor: "#BE2217",
                  color: "#BE2217",
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Delete ({selectedIds.length})
              </Button>
              <Button
                variant="contained"
                startIcon={<CancelRounded />}
                disabled={isSubmitting}
                onClick={handleBulkReject}
                sx={{
                  height: 40,
                  borderRadius: "96px",
                  bgcolor: "#BE2217",
                  color: "#FFF",
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#A31B12",
                    boxShadow: "none",
                  },
                }}
              >
                Reject ({selectedIds.length})
              </Button>
              <Chip
                clickable
                disabled={isSubmitting}
                onClick={handleBulkApprove}
                icon={<CheckCircleOutlined sx={{ fontSize: 18 }} />}
                label={
                  <Typography
                    component="span"
                    sx={{ fontSize: 16, lineHeight: 1.5, fontWeight: 700 }}
                  >
                    Approve{" "}
                    <Typography
                      component="span"
                      sx={{ fontSize: 16, lineHeight: 1.4, fontWeight: 400 }}
                    >
                      ({selectedIds.length})
                    </Typography>
                  </Typography>
                }
                sx={{
                  px: 2,
                  height: 40,
                  borderRadius: "100px",
                  bgcolor: "#D4EDFF",
                  color: "#0054FC",
                }}
              />
            </Stack>
          )}
        </Box>
        {isLoading ? (
          <TableLoadingSkeleton columns={LOCATIONS_SKELETON_COLUMNS} />
        ) : filteredAddresses.length === 0 ? (
          <Box sx={{ borderTop: `1px solid ${theme.vars.palette.divider}` }}>
            <EmptyState variant="addresses" />
          </Box>
        ) : (
          renderTable()
        )}
      </Paper>

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleCloseActionMenu}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              borderRadius: "8px",
              boxShadow:
                "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
          list: {
            sx: { p: 0 },
          },
        }}
      >
        <MenuItem
          onClick={handleActionView}
          sx={{
            minHeight: 40,
            px: 3,
            py: 1,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#25272C",
            "&:hover": { bgcolor: "#EDEEF1" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
            <VisibilitySharp sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="View"
            slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleActionEdit}
          sx={{
            minHeight: 40,
            px: 3,
            py: 1,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#25272C",
            "&:hover": { bgcolor: "#EDEEF1" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
            <EditSharp sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Edit"
            slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleActionDelete}
          sx={{
            minHeight: 40,
            px: 3,
            py: 1,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#25272C",
            "&:hover": { bgcolor: "#EDEEF1" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
            <DeleteSharp sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
          />
        </MenuItem>
      </Menu>

      <Dialog
        open={viewDialogOpen}
        onClose={() =>
          dispatch({ type: "SET_VIEW_DIALOG_OPEN", payload: false })
        }
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 1,
            overflow: "hidden",
            boxShadow:
              "0px 1px 3px 0px rgba(0,0,0,0.12), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogTitle sx={{ px: 2, py: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#25272C" }}>
              Shipping Address - View Details
            </Typography>
            <IconButton
              onClick={() =>
                dispatch({ type: "SET_VIEW_DIALOG_OPEN", payload: false })
              }
              size="small"
              sx={{ color: "#25272C" }}
            >
              <CloseSharp />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pt: 0, pb: 0 }}>
          {selectedAddress && (
            <Stack spacing={3} sx={{ pt: 0.5, pb: 2 }}>
              <Stack spacing={1}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#25272C" }}
                >
                  Primary Address
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #D8DBDF",
                    borderRadius: "4px",
                    p: 2,
                  }}
                >
                  <Stack spacing={1}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "#111" }}
                    >
                      {selectedAddress.ecp_code} {selectedAddress.shop_name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#25272C" }}>
                      {formatThaiPhone(selectedAddress.phone, "-")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "#111", whiteSpace: "pre-wrap" }}
                    >
                      {getFullAddressText(selectedAddress)}
                    </Typography>
                    <Chip
                      label="ECP Linked"
                      size="small"
                      sx={{
                        width: "fit-content",
                        height: 24,
                        borderRadius: "4px",
                        bgcolor: "#EDEEF1",
                        color: "#25272C",
                        "& .MuiChip-label": {
                          px: 1,
                          fontSize: 12,
                          fontWeight: 400,
                          lineHeight: 1.4,
                        },
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>

              <Stack spacing={2}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#111" }}
                >
                  Request by
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                  <Stack spacing={0.5} sx={{ flex: 1, py: 1 }}>
                    <Typography variant="body2" sx={{ color: "#8E95A2" }}>
                      ECP CODE
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#101828" }}>
                      {selectedAddress.ecp_code || "-"}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.5} sx={{ flex: 1, py: 1 }}>
                    <Typography variant="body2" sx={{ color: "#8E95A2" }}>
                      Line Name
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#101828" }}>
                      {selectedAddress.created_by?.name || "-"}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack spacing={0.5} sx={{ py: 1 }}>
                  <Typography variant="body2" sx={{ color: "#8E95A2" }}>
                    Name Customer
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#101828" }}>
                    {selectedAddress.name} {selectedAddress.surname}
                  </Typography>
                </Stack>
              </Stack>

              <Stack spacing={2.5} sx={{ pb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#111" }}
                >
                  Shipping Request
                </Typography>
                <Stack spacing={1}>
                  <Typography
                    variant="body1"
                    sx={{ color: "#25272C" }}
                  >
                    ECP CODE
                    <Typography component="span" sx={{ color: "#BE2217" }}>
                      *
                    </Typography>
                  </Typography>
                  <TextField
                    value={selectedAddress.ecp_code}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
                <Stack spacing={1}>
                  <Typography
                    variant="body1"
                    sx={{ color: "#25272C" }}
                  >
                    Store Name
                    <Typography component="span" sx={{ color: "#BE2217" }}>
                      *
                    </Typography>
                  </Typography>
                  <TextField
                    value={selectedAddress.shop_name}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
                <Stack spacing={1}>
                  <Typography
                    variant="body1"
                    sx={{ color: "#25272C" }}
                  >
                    Phone Number
                    <Typography component="span" sx={{ color: "#BE2217" }}>
                      *
                    </Typography>
                  </Typography>
                  <TextField
                    value={selectedAddress.phone}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
                <Stack spacing={1}>
                  <Typography
                    variant="body1"
                    sx={{ color: "#25272C" }}
                  >
                    Address Details
                    <Typography component="span" sx={{ color: "#BE2217" }}>
                      *
                    </Typography>
                  </Typography>
                  <TextField
                    value={selectedAddress.address}
                    fullWidth
                    multiline
                    minRows={2}
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                  <Stack spacing={1} sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                    sx={{ color: "#25272C" }}
                    >
                      Province
                      <Typography component="span" sx={{ color: "#BE2217" }}>
                        *
                      </Typography>
                    </Typography>
                    <TextField
                      value={selectedAddress.province}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </Stack>
                  <Stack spacing={1} sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                    sx={{ color: "#25272C" }}
                    >
                      District
                      <Typography component="span" sx={{ color: "#BE2217" }}>
                        *
                      </Typography>
                    </Typography>
                    <TextField
                      value={selectedAddress.district}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </Stack>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                  <Stack spacing={1} sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                    sx={{ color: "#25272C" }}
                    >
                      Subdistrict
                      <Typography component="span" sx={{ color: "#BE2217" }}>
                        *
                      </Typography>
                    </Typography>
                    <TextField
                      value={selectedAddress.sub_district}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </Stack>
                  <Stack spacing={1} sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                    sx={{ color: "#25272C" }}
                    >
                      Postal Code
                      <Typography component="span" sx={{ color: "#BE2217" }}>
                        *
                      </Typography>
                    </Typography>
                    <TextField
                      value={selectedAddress.zip_code}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <Divider sx={{ borderColor: "#D8DBDF" }} />
        <Box
          sx={{
            px: 2,
            py: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleDetailDelete}
            disabled={isSubmitting || !selectedAddress}
            sx={{
              height: 40,
              px: 3,
              borderRadius: "96px",
              borderColor: "#BE2217",
              color: "#BE2217",
              fontWeight: 700,
              fontSize: 16,
              textTransform: "none",
            }}
          >
            Delete Address
          </Button>
          {selectedAddress?.status === "pending" && (
            <>
              <Button
                variant="contained"
                onClick={handleDetailReject}
                disabled={isSubmitting}
                sx={{
                  height: 40,
                  px: 3,
                  borderRadius: "96px",
                  bgcolor: "#BE2217",
                  color: "#FFF",
                  fontWeight: 700,
                  fontSize: 16,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#A31B12", boxShadow: "none" },
                }}
              >
                Reject Address
              </Button>
              <Button
                onClick={handleDetailApprove}
                disabled={isSubmitting}
                sx={{
                  height: 40,
                  px: 3,
                  borderRadius: "100px",
                  bgcolor: "#D4EDFF",
                  color: "#0054FC",
                  fontWeight: 700,
                  fontSize: 16,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#BFDEFF" },
                }}
              >
                Approve
              </Button>
            </>
          )}
        </Box>
      </Dialog>

      <CustomDialog
        open={dialogOpen}
        title={selectedAddress ? "Edit Address" : "Add New Address"}
        onClose={() => dispatch({ type: "SET_DIALOG_OPEN", payload: false })}
        maxWidth="md"
        content={
          <AddressForm
            address={selectedAddress}
            onSubmit={handleFormSubmit}
            onCancel={() =>
              dispatch({ type: "SET_DIALOG_OPEN", payload: false })
            }
          />
        }
      />

      <CustomConfirmDialog
        open={deleteDialogOpen}
        title="Delete Address"
        onClose={() =>
          dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false })
        }
        content={
          <Typography>
            Are you sure you want to delete the address for{" "}
            <strong>{selectedAddress?.name}</strong>? This action cannot be
            undone.
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
                dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false })
              }
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              Delete
            </Button>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={bulkDeleteDialogOpen}
        title={`Delete Address Approval (${selectedIds.length})`}
        onClose={() =>
          dispatch({ type: "SET_BULK_DELETE_DIALOG_OPEN", payload: false })
        }
        content={
          <Typography sx={{ fontSize: 16, color: "#25272C" }}>
            Are you sure you want to delete{" "}
            <strong>{selectedIds.length}</strong> selected address
            {selectedIds.length > 1 ? "es" : ""}? This action cannot be undone.
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
                dispatch({
                  type: "SET_BULK_DELETE_DIALOG_OPEN",
                  payload: false,
                })
              }
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmBulkDelete}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#BE2217",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#A31B12" },
              }}
            >
              Delete
            </Button>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={approveDialogOpen}
        title={`Approve Address Approval (${selectedIds.length})`}
        onClose={() =>
          dispatch({ type: "SET_APPROVE_DIALOG_OPEN", payload: false })
        }
        content={
          <Typography sx={{ fontSize: 16, color: "#25272C" }}>
            Are you sure you want to approve{" "}
            <strong>{selectedIds.length}</strong> selected address
            {selectedIds.length > 1 ? "es" : ""}?
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
                dispatch({ type: "SET_APPROVE_DIALOG_OPEN", payload: false })
              }
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmApprove}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#2D6830",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#245626" },
              }}
            >
              Approve
            </Button>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={rejectDialogOpen}
        title={`Reject Address Approval (${selectedIds.length})`}
        onClose={() =>
          dispatch({ type: "SET_REJECT_DIALOG_OPEN", payload: false })
        }
        content={
          <Stack spacing={2}>
            <Typography sx={{ fontSize: 16, color: "#25272C" }}>
              Are you sure you want to reject{" "}
              <strong>{selectedIds.length}</strong> selected address
              {selectedIds.length > 1 ? "es" : ""}?
            </Typography>
            <TextField
              label="Remark"
              placeholder="Enter reason for rejection"
              multiline
              rows={3}
              fullWidth
              value={rejectRemark}
              onChange={(e) =>
                dispatch({ type: "SET_REJECT_REMARK", payload: e.target.value })
              }
            />
          </Stack>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
                dispatch({ type: "SET_REJECT_DIALOG_OPEN", payload: false })
              }
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmReject}
              disabled={isSubmitting || !rejectRemark.trim()}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#BE2217",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#A31B12" },
              }}
            >
              Reject
            </Button>
          </Stack>
        }
      />
    </Stack>
  );
}
